import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { parse, isPast, isToday } from "date-fns";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ShieldCheck, LogOut, Users, Store, CalendarDays, Megaphone, Mail, UserPlus, ListChecks } from "lucide-react";
import { BusinessManagement } from "@/components/admin/BusinessManagement";
import { MarketManagement } from "@/components/admin/MarketManagement";
import { EventManagement } from "@/components/admin/EventManagement";
import { MemberManagement } from "@/components/admin/MemberManagement";
import { SiteContentManagement } from "@/components/admin/SiteContentManagement";
import { SystemEmailManagement } from "@/components/admin/SystemEmailManagement";
import { Join614Signup } from "@/components/admin/Join614Signup";
import { EmailListsManagement } from "@/components/admin/EmailListsManagement";
import { NewsletterDraftsManagement } from "@/components/admin/NewsletterDraftsManagement";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tryParseDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    let date = parse(dateStr, "MMMM d, yyyy", new Date());
    if (!isNaN(date.getTime())) return date;
    date = new Date(dateStr);
    if (!isNaN(date.getTime())) return date;
  } catch { /* ignore */ }
  return null;
};

const isEventActive = (event) => {
  if (event.approval_status === "pending") return true;
  if (event.approval_status !== "approved") return false;
  const date = tryParseDate(event.date);
  if (!date) return true;
  return isToday(date) || !isPast(date);
};

const AdminDashboardPage = () => {
  const [businesses, setBusinesses] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSection = searchParams.get("section") || "businesses";
  const [activeSection, setActiveSection] = useState(initialSection === "newsletter-drafts" ? "emails" : initialSection);
  const [activeEmailTab, setActiveEmailTab] = useState(
    searchParams.get("emailTab") || (initialSection === "newsletter-drafts" ? "newsletters" : "system"),
  );
  const [mountedToolSections, setMountedToolSections] = useState(() => {
    return ["messages", "emails", "newsletter-drafts", "email-lists", "join"].includes(initialSection)
      ? [initialSection === "newsletter-drafts" ? "emails" : initialSection]
      : [];
  });
  const navigate = useNavigate();
  const token = localStorage.getItem("bizkeep_admin_token");

  useEffect(() => {
    if (!token) { navigate("/admin"); return; }
    fetchAll();
  }, [token, navigate]);

  useEffect(() => {
    const requestedSection = searchParams.get("section");
    if (["businesses", "markets", "events", "members", "messages", "emails", "newsletter-drafts", "email-lists", "join"].includes(requestedSection)) {
      const nextSection = requestedSection === "newsletter-drafts" ? "emails" : requestedSection;
      setActiveSection(nextSection);
      if (["messages", "emails", "email-lists", "join"].includes(nextSection)) {
        setMountedToolSections((previous) => previous.includes(nextSection) ? previous : [...previous, nextSection]);
      }
      if (requestedSection === "newsletter-drafts") setActiveEmailTab("newsletters");
    }
    const requestedEmailTab = searchParams.get("emailTab");
    if (["system", "newsletters"].includes(requestedEmailTab)) {
      setActiveEmailTab(requestedEmailTab);
    }
  }, [searchParams]);

  const fetchAll = () => {
    const headers = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/admin/businesses`, { headers }).then(r => setBusinesses(r.data)).catch(err => {
      if (err.response?.status === 401) { localStorage.removeItem("bizkeep_admin_token"); navigate("/admin"); }
    });
    axios.get(`${API}/admin/markets`, { headers }).then(r => setMarkets(r.data)).catch(() => {});
    axios.get(`${API}/admin/events`, { headers }).then(r => setEvents(r.data)).catch(() => {});
    axios.get(`${API}/admin/shoppers`, { headers }).then(r => setMembers(r.data)).catch(() => {});
  };

  const handleLogout = () => { localStorage.removeItem("bizkeep_admin_token"); navigate("/admin"); };
  const activeEventCount = events.filter(isEventActive).length;

  const mainSections = [
    { key: "businesses", label: "Businesses", icon: Users, count: businesses.length },
    { key: "markets", label: "Markets", icon: Store, count: markets.length },
    { key: "events", label: "Events", icon: CalendarDays, count: activeEventCount },
    { key: "members", label: "Members", icon: Users, count: members.length },
  ];

  const toolSections = [
    { key: "messages", label: "Messages", icon: Megaphone, count: 2 },
    { key: "emails", label: "Emails", icon: Mail, count: "Edit" },
    { key: "email-lists", label: "Email Lists", icon: ListChecks, count: "Lists" },
    { key: "join", label: "Join 614", icon: UserPlus, count: "Form" },
  ];

  const handleSectionChange = (section) => {
    setActiveSection(section);
    if (["messages", "emails", "email-lists", "join"].includes(section)) {
      setMountedToolSections((previous) => previous.includes(section) ? previous : [...previous, section]);
    }
    setSearchParams(section === "emails" ? { section, emailTab: activeEmailTab } : { section });
  };

  const handleEmailTabChange = (tab) => {
    setActiveEmailTab(tab);
    setSearchParams({ section: "emails", emailTab: tab });
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-50 bg-[#1a1a1a] text-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#42D78A] rounded-full flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-[#1a1a1a]" />
            </div>
            <div>
              <span className="font-bold text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>Admin Portal</span>
              <p className="text-xs text-gray-400" style={{ fontFamily: "'Montserrat', sans-serif" }}>614 Shop Local</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white hover:text-gray-300 hover:bg-white/10" data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8 space-y-3">
          <div className="flex flex-wrap items-center gap-2 bg-gray-100 rounded-2xl sm:rounded-full p-1 w-fit">
            {mainSections.map((s) => (
              <button
                key={s.key}
                onClick={() => handleSectionChange(s.key)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition-all ${
                  activeSection === s.key ? "bg-[#1a1a1a] text-white shadow-sm" : "text-gray-600 hover:text-[#1a1a1a]"
                }`}
                style={{ fontFamily: "'Outfit', sans-serif" }}
                data-testid={`admin-section-${s.key}`}
              >
                <s.icon className="w-4 h-4" /> {s.label} ({s.count})
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {toolSections.map((s) => (
              <button
                key={s.key}
                onClick={() => handleSectionChange(s.key)}
                className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-bold transition-all ${
                  activeSection === s.key
                    ? "border-[#1a1a1a] bg-[#1a1a1a] text-white shadow-sm"
                    : "border-gray-200 bg-white text-gray-600 hover:border-[#42D78A] hover:text-[#1a1a1a]"
                }`}
                style={{ fontFamily: "'Outfit', sans-serif" }}
                data-testid={`admin-section-${s.key}`}
              >
                <s.icon className="w-4 h-4" /> {s.label}
              </button>
            ))}
          </div>
        </div>

        {activeSection === "businesses" && <BusinessManagement businesses={businesses} token={token} onRefresh={fetchAll} />}
        {activeSection === "markets" && <MarketManagement markets={markets} token={token} onRefresh={fetchAll} />}
        {activeSection === "events" && <EventManagement events={events} token={token} onRefresh={fetchAll} businesses={businesses} markets={markets} />}
        {activeSection === "members" && <MemberManagement members={members} token={token} />}
        {mountedToolSections.includes("messages") && (
          <div className={activeSection === "messages" ? "block" : "hidden"}>
            <SiteContentManagement token={token} />
          </div>
        )}
        {mountedToolSections.includes("emails") && (
          <div className={activeSection === "emails" ? "block" : "hidden"}>
            <Tabs value={activeEmailTab} onValueChange={handleEmailTabChange} className="space-y-6">
              <TabsList className="h-auto flex-wrap justify-start rounded-full bg-gray-100 p-1">
                <TabsTrigger value="system" className="rounded-full px-5 py-2 font-bold data-[state=active]:bg-[#1a1a1a] data-[state=active]:text-white">
                  System Emails
                </TabsTrigger>
                <TabsTrigger value="newsletters" className="rounded-full px-5 py-2 font-bold data-[state=active]:bg-[#1a1a1a] data-[state=active]:text-white">
                  Newsletter Drafts
                </TabsTrigger>
              </TabsList>
              <TabsContent value="system" className="mt-0">
                <SystemEmailManagement token={token} />
              </TabsContent>
              <TabsContent value="newsletters" className="mt-0">
                <NewsletterDraftsManagement token={token} />
              </TabsContent>
            </Tabs>
          </div>
        )}
        {mountedToolSections.includes("email-lists") && (
          <div className={activeSection === "email-lists" ? "block" : "hidden"}>
            <EmailListsManagement token={token} />
          </div>
        )}
        {mountedToolSections.includes("join") && (
          <div className={activeSection === "join" ? "block" : "hidden"}>
            <Join614Signup token={token} />
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboardPage;
